import { useQuery, useMutation, useQueryClient, useQueries } from '@tanstack/react-query';
import { rentingsApi, RentingsParams, RentingsResponse, UpdateRentingDateTimeDto, UpdateRentingAddressDto, UpdateRentingDto, UpdateRentingResponse } from '../api/rentingsApi';
import { queryKeys, invalidateEntityQueries } from '@/shared/lib/react-query';

// Deprecated: используйте queryKeys.rentings вместо этого
export const RENTINGS_QUERY_KEY = ['rentings'];
export const RENTINGS_PER_PAGE = 20;

export function useRentings(page: number = 1, filters?: Partial<RentingsParams>) {
  return useQuery<RentingsResponse>({
    queryKey: queryKeys.rentings.list(page, filters),
    queryFn: async () => {
      return rentingsApi.getRentings({
        offset: (page - 1) * RENTINGS_PER_PAGE,
        limit: RENTINGS_PER_PAGE,
        status: filters?.status,
        car: filters?.car,
        client: filters?.client
      });
    }
  });
}

/**
 * Получить аренду по ID
 */
export function useRenting(id: string) {
  return useQuery({
    queryKey: queryKeys.rentings.detail(id),
    queryFn: () => rentingsApi.getRentingById(id)
  });
}

/**
 * @deprecated Используйте useRenting вместо этого
 */
export function useRentingById(id: string) {
  return useRenting(id);
}

export function useConfirmRenting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rentingsApi.confirmRenting,
    onSuccess: () => {
      invalidateEntityQueries.rentings(queryClient);
    }
  });
}

export function useCancelRenting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => rentingsApi.cancelRenting(id),
    onSuccess: () => {
      invalidateEntityQueries.rentings(queryClient);
    },
  });
}

export function useUpdateRentingStartDateTime() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ rentingId, data }: { rentingId: string; data: UpdateRentingDateTimeDto }) =>
      rentingsApi.updateRentingStartDateTime(rentingId, data),
    onSuccess: async (_, { rentingId }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentings.detail(rentingId) });
      await invalidateEntityQueries.rentings(queryClient);
    },
  });
}

export function useUpdateRentingEndDateTime() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ rentingId, data }: { rentingId: string; data: UpdateRentingDateTimeDto }) =>
      rentingsApi.updateRentingEndDateTime(rentingId, data),
    onSuccess: async (_, { rentingId }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentings.detail(rentingId) });
      await invalidateEntityQueries.rentings(queryClient);
    },
  });
}

export function useUpdateRentingDeliveryAddress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ rentingId, data }: { rentingId: string; data: UpdateRentingAddressDto }) =>
      rentingsApi.updateRentingDeliveryAddress(rentingId, data),
    onSuccess: async (_, { rentingId }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentings.detail(rentingId) });
      await invalidateEntityQueries.rentings(queryClient);
    },
  });
}

export function useUpdateRentingPickupAddress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ rentingId, data }: { rentingId: string; data: UpdateRentingAddressDto }) =>
      rentingsApi.updateRentingPickupAddress(rentingId, data),
    onSuccess: async (_, { rentingId }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentings.detail(rentingId) });
      await invalidateEntityQueries.rentings(queryClient);
    },
  });
}

export function useUpdateRenting() {
  const queryClient = useQueryClient();

  return useMutation<UpdateRentingResponse, Error, { rentingId: string; data: UpdateRentingDto }>({
    mutationFn: ({ rentingId, data }: { rentingId: string; data: UpdateRentingDto }) =>
      rentingsApi.updateRenting(rentingId, data),
    onSuccess: async (_, { rentingId }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.rentings.detail(rentingId) });
      await invalidateEntityQueries.rentings(queryClient);
    },
  });
}
